class MeriPredictiveSearch extends HTMLElement {
  connectedCallback() {
    if (this.listeners || this.supported === false) return;
    this.input = this.querySelector('[data-predictive-input]');
    this.panel = this.querySelector('[data-predictive-panel]');
    this.status = this.querySelector('[data-predictive-status]');
    this.form = this.closest('form');
    if (!this.input || !this.panel || !this.status || !this.form) return;
    try {
      const features = document.getElementById('shopify-features');
      if (features && JSON.parse(features.textContent).predictiveSearch === false) {
        this.useNativeSearch();
        return;
      }
    } catch {
      /* An unknown support flag can be checked by the native endpoint. */
    }
    this.listeners = new AbortController();
    this.sequence = this.sequence || 0;
    this.supported = true;
    this.composing = false;
    this.input.setAttribute('role', 'combobox');
    this.input.setAttribute('aria-autocomplete', 'list');
    this.input.setAttribute('aria-haspopup', 'listbox');
    this.input.setAttribute('aria-controls', this.panel.id + '-listbox');
    this.input.setAttribute('aria-expanded', 'false');
    const options = { signal: this.listeners.signal };
    this.input.addEventListener(
      'input',
      (event) => {
        if (!event.isComposing && !this.composing) this.schedule();
      },
      options,
    );
    this.input.addEventListener('focus', () => this.schedule(), options);
    this.input.addEventListener(
      'compositionstart',
      () => {
        this.composing = true;
        this.cancel();
      },
      options,
    );
    this.input.addEventListener(
      'compositionend',
      () => {
        this.composing = false;
        this.schedule();
      },
      options,
    );
    this.input.addEventListener('keydown', (event) => this.onKeydown(event), options);
    this.form.addEventListener('submit', () => this.cancel(), options);
    this.addEventListener(
      'focusout',
      (event) => {
        if (!this.contains(event.relatedTarget)) this.cancel();
      },
      options,
    );
  }

  disconnectedCallback() {
    this.cancel();
    this.listeners?.abort();
    this.listeners = null;
  }

  useNativeSearch() {
    this.supported = false;
    this.cancel();
    this.listeners?.abort();
    this.listeners = null;
    for (const attribute of ['role', 'aria-autocomplete', 'aria-haspopup', 'aria-controls'])
      this.input?.removeAttribute(attribute);
  }

  cancel() {
    this.sequence = (this.sequence || 0) + 1;
    clearTimeout(this.debounce);
    clearTimeout(this.timeout);
    this.request?.abort();
    this.request = null;
    this.active = -1;
    if (this.panel) {
      this.panel.hidden = true;
      this.panel.replaceChildren();
      this.panel.removeAttribute('aria-busy');
    }
    if (this.supported === false) this.input?.removeAttribute('aria-expanded');
    else this.input?.setAttribute('aria-expanded', 'false');
    this.input?.removeAttribute('aria-activedescendant');
    if (this.status) this.status.textContent = '';
  }

  schedule() {
    this.cancel();
    const query = this.input.value.trim();
    if (!query || !this.supported || this.composing) return;
    if (Date.now() < (this.retryAfter || 0)) {
      this.status.textContent = this.dataset.error;
      return;
    }
    const sequence = this.sequence;
    this.debounce = setTimeout(() => this.search(query, sequence), 200);
  }

  async search(query, sequence) {
    if (!this.isConnected || sequence !== this.sequence) return;
    const controller = new AbortController();
    this.request = controller;
    this.status.textContent = this.dataset.loading;
    this.panel.setAttribute('aria-busy', 'true');
    this.timeout = setTimeout(() => controller.abort(), 6000);
    try {
      const url = new URL(this.dataset.url, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid search route');
      url.searchParams.set('q', query);
      url.searchParams.set('section_id', 'predictive-search');
      url.searchParams.set('resources[type]', 'product,collection,page,article');
      url.searchParams.set('resources[limit]', '6');
      url.searchParams.set('resources[limit_scope]', 'all');
      const response = await fetch(url, {
        signal: controller.signal,
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });
      if (sequence !== this.sequence || controller.signal.aborted) return;
      if (response.status === 417) {
        // The endpoint can reject the buyer locale even when the feature flag is enabled.
        this.useNativeSearch();
        return;
      }
      if (response.status === 429)
        this.retryAfter =
          Date.now() + Math.min(60, Math.max(1, Number(response.headers.get('Retry-After')) || 1)) * 1000;
      if (!response.ok) throw new Error('Suggestions unavailable');
      const text = await response.text();
      if (
        !this.isConnected ||
        sequence !== this.sequence ||
        controller.signal.aborted ||
        this.input.value.trim() !== query ||
        !this.contains(document.activeElement)
      )
        return;
      const results = new DOMParser().parseFromString(text, 'text/html').querySelector('[data-predictive-response]');
      if (!results?.querySelector('[role="listbox"]')) throw new Error('Missing search results');
      results.querySelector('[role="listbox"]').id = this.panel.id + '-listbox';
      this.panel.replaceChildren(...results.childNodes);
      this.panel.querySelectorAll('[role="option"]').forEach((option, index) => {
        option.id = this.input.id + '-option-' + index;
      });
      this.panel.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.status.textContent = results.dataset.status || '';
    } catch {
      if (this.isConnected && sequence === this.sequence && this.request === controller)
        this.status.textContent = this.dataset.error;
    } finally {
      if (this.request === controller) {
        clearTimeout(this.timeout);
        this.request = null;
        this.panel.removeAttribute('aria-busy');
      }
    }
  }

  onKeydown(event) {
    if (event.isComposing || this.composing) return;
    if (event.key === 'Escape') {
      // Escape first closes open suggestions; with none open it reaches a containing dialog.
      if (this.panel.hidden && !this.request) return;
      event.preventDefault();
      this.cancel();
      return;
    }
    if (this.panel.hidden) return;
    const options = [...this.panel.querySelectorAll('[role="option"]')];
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (!options.length) return;
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      this.active =
        this.active < 0
          ? direction > 0
            ? 0
            : options.length - 1
          : (this.active + direction + options.length) % options.length;
      options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.active)));
      this.input.setAttribute('aria-activedescendant', options[this.active].id);
      options[this.active].scrollIntoView({ block: 'nearest' });
    } else if (event.key === 'Enter' && this.active >= 0) {
      const link = options[this.active]?.querySelector('a[href]');
      if (link) {
        event.preventDefault();
        link.click();
      }
    }
  }
}

if (!customElements.get('meri-predictive-search'))
  customElements.define('meri-predictive-search', MeriPredictiveSearch);
